import React from 'react';
import { FiAlertTriangle, FiBook, FiUsers } from 'react-icons/fi';

export function ContentThemeStep({ formData, onChange, errors }) {
  const contentThemes = [
    {
      value: 'agitate',
      label: 'Agitate',
      icon: FiAlertTriangle,
      description: 'Problem-focused content that highlights pain points',
      examples: [
        '"The Hidden Costs of Legacy Payment Systems"',
        '"Why Fuel Retailers Are Losing Revenue to Payment Friction"',
        '"The Integration Nightmare That\'s Costing Parking Operators"'
      ],
      tone: 'Urgent, problem-focused, emphasizes consequences of inaction',
      bestFor: 'Early-stage prospects who may not fully understand their problems'
    },
    {
      value: 'educate',
      label: 'Educate',
      icon: FiBook,
      description: 'Solution-focused educational content',
      examples: [
        '"Complete Guide to Modern Payment Orchestration"',
        '"Best Practices for EV Charging Payment Integration"',
        '"How Cloud-Native Payment Solutions Transform Operations"'
      ],
      tone: 'Informative, authoritative, thought-leadership focused',
      bestFor: 'Prospects researching solutions and evaluating options'
    },
    {
      value: 'engage',
      label: 'Engage',
      icon: FiUsers,
      description: 'Partnership and collaboration-focused',
      examples: [
        '"Success Stories: How TNS Transformed Parking Operations"',
        '"Partnership Opportunities in Unattended Retail"',
        '"Working with TNS: Your Implementation Roadmap"'
      ],
      tone: 'Collaborative, relationship-building, success-oriented',
      bestFor: 'Qualified prospects ready to engage with vendors'
    }
  ];

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Content Theme Selection</h2>
        <p className="text-gray-600 mb-8">
          Choose the primary approach for your marketing content. Each theme serves different stages of the buyer's journey and creates different emotional responses.
        </p>
      </div>

      {errors.contentTheme && (
        <div className="mb-4">
          <p className="text-red-600 text-sm">{errors.contentTheme}</p>
        </div>
      )}

      <div className="space-y-6">
        {contentThemes.map((theme) => {
          const IconComponent = theme.icon;
          return (
            <div
              key={theme.value}
              className={`relative rounded-lg border-2 p-6 cursor-pointer transition-all hover:shadow-md ${
                formData.contentTheme === theme.value
                  ? 'border-green-500 bg-green-50 shadow-sm'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => onChange('contentTheme', theme.value)}
            >
              <div className="flex items-start">
                <input
                  type="radio"
                  name="contentTheme"
                  value={theme.value}
                  checked={formData.contentTheme === theme.value}
                  onChange={() => onChange('contentTheme', theme.value)}
                  className="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300 mt-1"
                />
                <div className="ml-4 flex-1">
                  <div className="flex items-center mb-3">
                    <IconComponent className={`w-6 h-6 mr-3 ${
                      formData.contentTheme === theme.value ? 'text-green-600' : 'text-gray-400'
                    }`} />
                    <h3 className="text-lg font-semibold text-gray-900">
                      {theme.label}
                    </h3>
                  </div>
                  
                  <p className="text-gray-600 mb-4">{theme.description}</p>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Example Titles:</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {theme.examples.map((example, index) => (
                          <li key={index} className="italic">• {example}</li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Tone & Approach:</h4>
                      <p className="text-sm text-gray-600 mb-3">{theme.tone}</p>
                      
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Best For:</h4>
                      <p className="text-sm text-gray-600">{theme.bestFor}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {formData.contentTheme && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-2">Selected Theme</h4>
          <p className="text-sm text-blue-700">
            Your content will follow the <strong>{contentThemes.find(t => t.value === formData.contentTheme)?.label}</strong> approach, 
            which is ideal for {contentThemes.find(t => t.value === formData.contentTheme)?.bestFor.toLowerCase()}.
          </p>
        </div>
      )}
    </div>
  );
}